import styled from '@emotion/styled';
import {
  container,
  device,
  sizes,
  typography,
  useMedia,
  inViewConfig,
  shouldForwardProp,
} from '@landsbankinn/ui';
import React, { Children } from 'react';
import { useInView } from 'react-intersection-observer';
import { animated, useSpring } from 'react-spring';
import type { ImgSource } from '../../components/img/Img';
import { Img } from '../../components/img/Img';

export enum ImageAlignment {
  FULLSCREEN = 'FULLSCREEN',
  CENTER = 'CENTER',
  RIGHT = 'RIGHT',
  LEFT = 'LEFT',
}

export enum FontSize {
  Big = 'Big',
  Normal = 'Normal',
  Small = 'Small',
}

export enum TitlePosition {
  Left = 'Left',
  Right = 'Right',
  Center = 'Center',
}
export enum TitleColor {
  White = 'White',
  Blue = 'Blue',
}

export interface HeroProps {
  alignment?: ImageAlignment;
  label?: string;
  title?: React.ReactNode;
  center?: boolean;
  bottom?: boolean;
  shadeOpacity?: number;
  smallerFont?: boolean;
  image?: ImgSource;
  actions?: React.ReactNode;
  isAnimated?: boolean;
  noMarginBottom?: boolean;
  titleWidth?: number;
  titlePosition?: TitlePosition;
  titleColor?: TitleColor;
  fontSize?: FontSize;
}

interface ImageShadeProps {
  alignment: ImageAlignment;
  shadeOpacity: number;
}

const isEmpty = (value: string | React.ReactNode) => {
  if (Children.count(value) === 0) {
    return true;
  }
  return String(value ?? '').trim() === '';
};

const full = [ImageAlignment.CENTER, ImageAlignment.FULLSCREEN];

const FullBackground = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<any>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${(props) => props.theme.colors.primary};

  background-image: ${(props) => props.theme.gradients.primary};
`;

const Host = styled.div<{
  alignment?: ImageAlignment;
  noMarginBottom?: boolean;
}>`
  position: relative;
  padding-bottom: 1px;

  margin-bottom: ${(props) => (props.noMarginBottom ? 0 : '62px')};
  color: ${(props) => props.theme.colors.background};

  @media ${device.tablet} {
    padding-top: 120px;
    margin-bottom: ${(props) => (props.noMarginBottom ? 0 : '60px')};

    ${(props) =>
      props.alignment === ImageAlignment.FULLSCREEN
        ? 'margin-top: 0px;'
        : 'margin-top: -80px;'};

    @media (max-height: 955px) {
      padding-top: 90px;
      margin-bottom: 30px;
    }
  }
`;

const Container = styled(animated.div)`
  @media ${device.tablet} {
    ${container}
  }
`;

const Image = styled.img<{ alignment?: ImageAlignment }>`
  object-fit: cover;
`.withComponent(Img);

const ImageShade = styled.div<ImageShadeProps>`
  @media ${device.tablet} {
    position: absolute;
    top: 0;
    ${(props) =>
      props.alignment === ImageAlignment.RIGHT ? 'right: 0px' : 'left: 0px'};
    width: ${(props) => (full.includes(props.alignment) ? '100%' : '50%')};
    height: 100%;

    background-image: linear-gradient(
      0deg,
      rgba(0, 0, 0, ${(props) => props.shadeOpacity || 0.1}),
      rgba(0, 0, 0, ${(props) => (props.shadeOpacity || 0.1) / 4})
    );
  }
`;

const AspectRatio = styled.div<HeroProps>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;

  margin-bottom: ${(props) =>
    props.alignment === ImageAlignment.CENTER ? -30 : 0}px;

  &:before {
    padding-bottom: 100%;
    content: '';
    display: ${(props) =>
      props.alignment === ImageAlignment.FULLSCREEN ? 'none' : 'block'};
    float: left;
  }

  &:after {
    display: table;
    content: '';
    clear: both;
  }

  @media ${device.tablet} {
    flex-direction: ${(props) =>
      props.alignment === ImageAlignment.RIGHT ? 'row-reverse' : 'row'};

    &:before {
      padding-bottom: ${() => (1 / (16 / 9)) * 100}%;
      content: '';
      display: block;
      float: left;

      transition: padding-bottom 200ms ease-in-out;

      @media (max-height: 955px) {
        padding-bottom: 64vh;
      }
    }

    &:after {
      display: table;
      content: '';
      clear: both;
    }
  }
`;

// Only when alignment is Center
const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: ${(props) => props.theme.gradients.primary};
`;

const ImageAspect = styled.div<HeroProps>`
  width: 100%;

  &:before {
    content: '';
    display: block;
    padding-top: 100%;
  }

  > :first-child {
    position: absolute;
    top: 0;
    ${(props) =>
      props.alignment === ImageAlignment.LEFT ? 'right: 0' : 'left: 0'};
    height: 100%;
    width: 100%;
  }

  @media ${device.tablet} {
    position: absolute;
    bottom: -31px;
  }
`;

const BackgroundImage = styled.img`
  @media ${device.tablet} {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`.withComponent(Img);

const Content = styled.div<HeroProps>`
  position: relative;
  padding: 45px 30px 55px;
  display: flex;
  flex-direction: column;
  ${(props) => (props.center ? 'align-items: center;' : '')}
  background-color: ${(props) => props.theme.colors.primary};

  @media ${device.mobile} {
    padding: 45px 44px 55px;
  }

  @media ${device.tablet} {
    padding: 115px 60px 30px;
    background-color: transparent;
    width: ${(props) =>
      [ImageAlignment.LEFT, ImageAlignment.RIGHT].includes(
        props.alignment as any,
      )
        ? 'calc(50% + 60px)'
        : '100%'};

    ${(props) => {
      switch (props.alignment) {
        case ImageAlignment.RIGHT:
          return 'padding-right: 0px;';
        case ImageAlignment.LEFT:
          return 'padding-left: 60px; padding-right: 0;';
      }
      return '';
    }}
  }
`;

const Copy = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<HeroProps>`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-bottom: 0;

  ${(props) =>
    props.center
      ? `
    align-items: center;
    > * { text-align: center; }
  `
      : ''}
  ${(props) => (props.bottom ? 'justify-content: flex-end;' : '')}

  color: ${(props) => props.theme.colors.background};

  @media ${device.tablet} {
    ${(props) =>
      [ImageAlignment.LEFT, ImageAlignment.RIGHT].includes(
        props.alignment as any,
      )
        ? 'justify-content: center;'
        : ''}

    ${(props) => (props.bottom ? 'justify-content: flex-end;' : '')}
  }
`;

const Label = styled.div`
  font-family: ${(props) => props.theme.font.familySerifCond};
  font-weight: 500;
  font-size: 30px;
  letter-spacing: 0.25px;
  line-height: 40px;
`;

const Title = styled.h1<HeroProps>`
  max-width: calc((690 / ${sizes.h2Size}) * 1em);
  margin-bottom: 120px;
  font-family: ${(props) => props.theme.font.familySerifCond};
  font-weight: 500;
  font-size: 44px;
  line-height: ${57 / 44};
  color: 'inherit';

  @media ${device.tablet} {
    color: ${(props) =>
      props.titleColor === TitleColor.Blue
        ? props.theme.colors.primary
        : 'inherit'};

    ${(props) =>
      props.fontSize === FontSize.Big
        ? typography.h1
        : props.fontSize === FontSize.Normal
        ? typography.heroSmall
        : typography.h2}
    margin-bottom: calc((60 / ${sizes.h2Size}) * 1em);

    ${(props) => {
      switch (props.alignment) {
        case ImageAlignment.LEFT:
          return `max-width: 90%;`;
        case ImageAlignment.RIGHT:
          return `max-width: 100%;`;
        case ImageAlignment.CENTER:
          return `max-width: 64%;`;
      }
    }}
  }
  @media ${device.desktop} {
    margin-bottom: 60px;
    ${(props) =>
      props.titlePosition === TitlePosition.Left
        ? ''
        : props.titlePosition === TitlePosition.Center
        ? 'align-self:center;text-align:center;'
        : 'align-self:flex-end;text-align:right;'};

    max-width: ${(props) => `${((props.titleWidth as any) / 12) * 100}%`};
  }
`;

const Actions = styled(animated.div)<{
  alignment?: ImageAlignment;
  titlePosition?: TitlePosition;
  twoButtons?: boolean;
}>`
  display: flex;
  flex-direction: column;
  ${(props) => (props.twoButtons ? 'margin-bottom:-60px;' : 'margin-bottom:0')};

  margin-bottom: -88px;

  a {
    width: 100%;
    outline-offset: -0.4rem;
  }

  @media ${device.tablet} {
    margin-bottom: 0;
    a {
      width: auto;
      outline-offset: -0.4rem;
    }
  }

  > *:nth-child(2) {
    margin-top: 30px;
  }
  ${(props) =>
    props.titlePosition === TitlePosition.Left
      ? 'align-items: flex-start;'
      : props.titlePosition === TitlePosition.Center
      ? 'align-self:center;'
      : 'align-self:flex-end;'};
`;

const Column = styled.div`
  position: relative;
  width: 100%;

  @media ${device.tablet} {
    width: 51%;
  }
`;

export function Hero(props: HeroProps) {
  const [ref, inView] = useInView(inViewConfig);

  const {
    isAnimated,
    image,
    label,
    title,
    alignment,
    actions,
    smallerFont,
    center,
    bottom,
    noMarginBottom,
    shadeOpacity = 0.1,
    fontSize,
    titlePosition,
    titleColor,
    titleWidth,
  } = props;
  const isColumn =
    [ImageAlignment.LEFT, ImageAlignment.RIGHT].includes(alignment as any) &&
    !!image;

  const visible = isAnimated ?? inView;
  const delay = 300;
  const isMobile = useMedia('sm');
  const isTablet = useMedia('md');

  const backgroundStyle = useSpring({
    y: visible ? '0%' : '-100%',
  });

  const copyStyle = useSpring({
    opacity: visible ? 1 : 0,
    y: visible ? 0 : 20,
    delay,
  });

  const actionsStyle = useSpring({
    opacity: visible ? 1 : 0,
    overflow: 'hidden',
    delay: delay + 100,
    config: {
      tension: 320,
    },
  });

  const imageStyle = useSpring({
    x: visible ? '0%' : '101%',
  });

  return (
    <Host
      ref={ref}
      id="hero"
      alignment={alignment}
      noMarginBottom={noMarginBottom}
    >
      <FullBackground style={backgroundStyle} />
      <Container>
        {alignment === ImageAlignment.FULLSCREEN && !!image && (
          <>
            <BackgroundImage
              src={image}
              ar={isMobile || isTablet ? 'ar_1_1' : 'ar_16_9'}
              alt=""
            />
            <ImageShade shadeOpacity={shadeOpacity} alignment={alignment} />
          </>
        )}
        <AspectRatio alignment={alignment}>
          {alignment === ImageAlignment.CENTER && <Background />}
          {isColumn && (
            <Column>
              <ImageAspect alignment={alignment}>
                <animated.div
                  style={{
                    overflow: 'hidden',
                    transform: imageStyle.x.to((v) => `translateX(-${v})`),
                  }}
                >
                  <Image
                    src={image}
                    ar="ar_1_1"
                    alignment={alignment}
                    containerStyle={imageStyle}
                  />
                </animated.div>
              </ImageAspect>
            </Column>
          )}
          {alignment === ImageAlignment.CENTER && !!image && (
            <>
              <animated.div
                style={{
                  overflow: 'hidden',
                  transform: imageStyle.x.to((v) => `translateX(-${v})`),
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                }}
              >
                <BackgroundImage
                  src={image}
                  ar={isMobile ? 'ar_1_1' : 'ar_16_9'}
                  containerStyle={imageStyle}
                />
              </animated.div>
              <ImageShade shadeOpacity={shadeOpacity} alignment={alignment} />
            </>
          )}
          <Content alignment={alignment} center={center}>
            <Copy
              center={center}
              bottom={bottom}
              style={copyStyle}
              alignment={alignment}
            >
              {!isEmpty(label) && <Label>{label}</Label>}
              {!isEmpty(title) && (
                <Title
                  center={center}
                  alignment={alignment}
                  image={image}
                  smallerFont={smallerFont}
                  fontSize={fontSize}
                  titleWidth={titleWidth}
                  titlePosition={titlePosition}
                  titleColor={titleColor}
                >
                  {title}
                </Title>
              )}
              {!isEmpty(actions) && (
                <Actions
                  alignment={alignment}
                  style={actionsStyle}
                  titlePosition={titlePosition}
                >
                  {actions}
                </Actions>
              )}
            </Copy>
          </Content>
        </AspectRatio>
      </Container>
    </Host>
  );
}
